import React from "react";
import { useFormContext } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { labelForKey } from "@/components/enrichment/field-rules";
import { Slider } from "@/components/ui/slider";

export type FeaturesFieldCustomData = {
  /** Dimension label by key; the key itself is de-camel-cased without it. */
  featureLabels?: Record<string, string>;
};

/** The 0-1 feature map, as the entity's DTO stores it. */
type FeatureMap = Record<string, number | null | undefined>;

function asFeatureMap(value: unknown): FeatureMap {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as FeatureMap)
    : {};
}

/**
 * Generic 0-1 slider grid over a `Record<string, number>` feature map.
 * Register globally as the `features` fieldType.
 *
 * Schema usage:
 *   features: z.record(z.string(), z.number().nullable())
 *     .superRefine(fieldConfig({ fieldType: "features",
 *       customData: { featureLabels: ARTIST_FEATURE_LABELS } }))
 *
 * This used to be typed to `ArtistFeatures` with a hardcoded eight-key default,
 * so seven of the nine enriched entities — venue (12), event (23), genre (8),
 * movie, person, show-genre — had no editor here at all. It now reads the keys
 * off the value, and the labels off `customData.featureLabels`; a key the label
 * map does not name still gets a slider, under a de-camel-cased label.
 */
const FeaturesField: React.FC<AutoFormFieldProps> = ({
  id,
  value,
  field,
  inputProps,
}) => {
  const form = useFormContext();
  const labels =
    (field.fieldConfig?.customData as FeaturesFieldCustomData | undefined)
      ?.featureLabels ?? {};
  const current = asFeatureMap(value);

  // The label map first (its order is the DTO's own), then anything extra the
  // stored value carries — an unlisted dimension must not vanish on save.
  const keys = [...new Set([...Object.keys(labels), ...Object.keys(current)])];

  const setFeature = (key: string, next: number) => {
    const updated = { ...current, [key]: next };
    form.setValue(id, updated, { shouldDirty: true });
    inputProps.onChange({
      target: { name: id, value: updated },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  if (keys.length === 0) return null;

  return (
    <div className="space-y-3 pt-4">
      {keys.map((key) => {
        const stored = current[key];
        const score =
          typeof stored === "number" && Number.isFinite(stored) ? stored : 0;
        const percentage = Math.round(score * 100);
        return (
          <div className="flex items-center gap-3 py-1" key={key}>
            <span className="text-sm text-foreground w-40 shrink-0 truncate">
              {labels[key] ?? labelForKey(key)}
            </span>
            <Slider
              aria-label={labels[key] ?? labelForKey(key)}
              min={0}
              max={100}
              step={1}
              value={[percentage]}
              onValueChange={(val) => setFeature(key, (val[0] ?? 0) / 100)}
            />
            <span className="text-xs text-muted-foreground w-10 text-right font-mono">
              {percentage}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

export { FeaturesField };
export default FeaturesField;
